import { ChangeDetectionStrategy, Component, Input, OnInit, inject } from '@angular/core';
import { FormBuilder, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ModalController, PopoverController } from '@ionic/angular';
import { addDays, nextFriday, nextSunday } from 'date-fns';
import { Subject, Observable } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';
import { ToastService } from '../../_core/services/toast.service';
import { DatetimeComponent } from '../../_shared/components/datetime/datetime.component';
import { LeagueService } from '../league.service';
import { AdminService } from '../../admin/admin.service';
import { Organisation } from '../../_shared/models/csTypes/organisation';
import { League } from '../../_shared/models/league.model';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  standalone: false,
  selector: 'app-create-league',
  templateUrl: './create-league.component.html',
  styleUrls: ['./create-league.component.scss']
})

export class CreateLeagueComponent implements OnInit {
  private adminService = inject(AdminService);
  private modalController = inject(ModalController);
  private toast = inject(ToastService);
  private leagueService = inject(LeagueService);
  private fb = inject(FormBuilder);
  private popoverController = inject(PopoverController);

  @Input() league: League;
  @Input() isEdit: boolean;

  form: UntypedFormGroup;
  isSubmitting: boolean;
  organisations$: Observable<Organisation[]> =  this.adminService.getOrganisations();
  organisations = toSignal(this.organisations$);
  organisationPolicy: any

  private readonly destroy$: Subject<void> = new Subject();

  constructor( ) { }

  ngOnInit() {
    this.buildForm();
  }

  private buildForm() {
    const today = new Date();
    today.setHours(12, 0, 0, 0)

    this.form = this.fb.group({
      startDate: [this.league?.startDate || nextFriday(today), Validators.required],
      endDate: [this.league?.endDate || nextSunday(nextFriday(today)), Validators.required],
      name: [this.league?.name, Validators.required],
      urlSegment: [this.league?.urlSegment || '', [Validators.required, this.urlSegmentValidation]],
      isPublic: [this.league?.isPublic],
      organisation: [this.league?.organisation?._id],
      leagueId: [this.league?.leagueId],
      policy: []
    })

    this.form.get('name').valueChanges.pipe(
      takeUntil(this.destroy$),
    ).subscribe(
        (value: string) => {
          // Check if URL segment has been changed by a user
          if (this.form.get('urlSegment').pristine) {
            // Replace all spaces with dash
            // Make url lower case
            const urlSegement: string = value.replace(/\s+/g, '-').toLowerCase();
            this.form.get('urlSegment').setValue(urlSegement);
          }
        }
      )
    
    this.form.get('startDate').valueChanges.pipe(
      takeUntil(this.destroy$),
    ).subscribe(
      startDate => {        
        if (new Date(startDate).getTime() > new Date(this.form.get('endDate').value).getTime()) {
          this.form.get('endDate').setValue(addDays(new Date(startDate), 2))
        }
      }
    )

    if (this.isEdit) {
      this.form.get('urlSegment').markAsDirty()
    }
  }

  async createLeague() {
    if (this.form.invalid) return;

    if(this.isEdit){
        // Use patchValue to update the form's policy field
        if(this.league?.organisation?._id === this.form.value.organisation){
            this.form.patchValue({
                policy: this.league?.policy?._id ? this.league?.policy?._id : null
            });
        }
    }

    this.isSubmitting = true;
    this.leagueService
        .create({ league: this.form.value })
        .pipe(
          takeUntil(this.destroy$),
          finalize(() => this.isSubmitting = false)
        )
        .subscribe(
            res => {
                this.modalController.dismiss(res.data);
                this.toast.presentToast(res.message)
            }
        );
  }

  async changeDatetime(formControlName: string) {
    const popover = await this.popoverController.create({
        component: DatetimeComponent,
        componentProps: {
            dateTimeIsoString: this.form.get(formControlName).value,
            presentation: 'date'
        },
        translucent: false,
        cssClass: 'mt-popover'
    });

    await popover.present();

    const { data } = await popover.onDidDismiss();

    if (data) {
        this.form.get(formControlName).setValue(data);
    }
}

  async close() {
    await this.modalController.dismiss();
  }

  urlSegmentValidation(control: UntypedFormControl) {
    const urlSegment: string = control.value;

    if (/\s/.test(urlSegment)) {
      return 'Contains space'
    }

    if (urlSegment.length === 12 || urlSegment.length === 24) {
      return 'URL segment cannot be 12 or 24 characters'
    }

    return null;
  }

  onOrganisationSelected(event: any) {
    const selectedOrganisationId = event.detail.value; // Get the selected value
    const selectedOrganisation = this.organisations()?.find(
        (o) => o._id === selectedOrganisationId
      );
      this.organisationPolicy = selectedOrganisation?.policies;
      if(!this.organisationPolicy.length){
        this.form.patchValue({ policy: null});
      }
  }

  get leagueActionText(): string {
    return this.isEdit ? 'Update League' : 'Create League';
  }
}
